import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ScrollArea } from './ui/scroll-area';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import {
  DollarSign, TrendingUp, Users, Building2, HeartPulse, Mail, Send, Plus,
  Loader2, MessageSquare, ThumbsUp, ThumbsDown, Minus, RefreshCw,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const fmtMoney = (n) => `$${(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

function StatCard({ icon: Icon, label, value, sub, color = '#7c3aed' }) {
  return (
    <div className="border rounded-xl p-4 bg-card">
      <div className="flex items-center gap-2 mb-1">
        <Icon className="h-4 w-4" style={{ color }} />
        <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">{label}</span>
      </div>
      <div className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

// ═══ 1. REVENUE DASHBOARD ═══
export function RevenueDashboard({ token }) {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/admin/revenue-dashboard`, { headers })
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load revenue data'))
      .finally(() => setLoading(false));
  }, [headers]);

  if (loading) return <div className="py-16 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" /></div>;
  if (!data) return <p className="p-6 text-sm text-muted-foreground">No revenue data available.</p>;

  return (
    <ScrollArea className="h-full">
      <div className="p-6 max-w-5xl space-y-6" data-testid="sa-revenue-dashboard">
        <div>
          <h2 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>Revenue Dashboard</h2>
          <p className="text-xs text-muted-foreground">MRR / ARR tracking across all tenants</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard icon={DollarSign} label="MRR" value={fmtMoney(data.mrr)} color="#059669" />
          <StatCard icon={TrendingUp} label="ARR" value={fmtMoney(data.arr)} color="#7c3aed" />
          <StatCard icon={Building2} label="Tenants" value={data.total_tenants} sub={`${data.total_paying} paying`} color="#2563eb" />
          <StatCard icon={Users} label="Paying Rate" value={`${Math.round((data.total_paying / Math.max(data.total_tenants, 1)) * 100)}%`} color="#d97706" />
        </div>

        {data.by_salesperson?.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold mb-2">By Salesperson</h3>
            <div className="border rounded-xl divide-y">
              {data.by_salesperson.map(sp => (
                <div key={sp.name} className="flex items-center gap-3 p-3 text-sm">
                  <span className="font-medium flex-1">{sp.name}</span>
                  <span className="text-xs text-muted-foreground">{sp.tenants} tenants · {sp.rooms.toLocaleString()} rooms</span>
                  <span className="font-bold tabular-nums">{fmtMoney(sp.mrr)}/mo</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div>
          <h3 className="text-sm font-semibold mb-2">Tenant Revenue</h3>
          <div className="border rounded-xl overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-muted/40">
                <tr className="text-left text-muted-foreground">
                  <th className="p-2.5 font-semibold">Tenant</th>
                  <th className="p-2.5 font-semibold">Plan</th>
                  <th className="p-2.5 font-semibold text-right">Rooms</th>
                  <th className="p-2.5 font-semibold text-right">Properties</th>
                  <th className="p-2.5 font-semibold">Contract End</th>
                  <th className="p-2.5 font-semibold text-right">Monthly</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {data.tenants.map(t => (
                  <tr key={t.tenant_id} className="hover:bg-muted/20" data-testid={`revenue-row-${t.tenant_id}`}>
                    <td className="p-2.5 font-medium">{t.name}</td>
                    <td className="p-2.5"><Badge variant="outline" className="text-[9px] capitalize">{t.plan || '—'}</Badge></td>
                    <td className="p-2.5 text-right tabular-nums">{t.rooms.toLocaleString()}</td>
                    <td className="p-2.5 text-right tabular-nums">{t.properties}</td>
                    <td className="p-2.5 text-muted-foreground">{t.contract_end ? t.contract_end.slice(0, 10) : '—'}</td>
                    <td className="p-2.5 text-right font-bold tabular-nums">{fmtMoney(t.monthly)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </ScrollArea>
  );
}

// ═══ 2. CUSTOMER HEALTH ═══
const HEALTH_COLORS = { healthy: '#059669', watch: '#d97706', at_risk: '#dc2626' };

export function CustomerHealth({ token }) {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/admin/customer-health`, { headers })
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load health scores'))
      .finally(() => setLoading(false));
  }, [headers]);

  if (loading) return <div className="py-16 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" /></div>;
  if (!data) return <p className="p-6 text-sm text-muted-foreground">No health data available.</p>;

  const s = data.summary || {};

  return (
    <ScrollArea className="h-full">
      <div className="p-6 max-w-5xl space-y-6" data-testid="sa-customer-health">
        <div>
          <h2 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>Customer Health</h2>
          <p className="text-xs text-muted-foreground">Per-tenant health from logins, adoption, usage & renewal proximity</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard icon={HeartPulse} label="Avg Score" value={s.avg_score ?? '—'} color="#7c3aed" />
          <StatCard icon={ThumbsUp} label="Healthy" value={s.healthy ?? 0} color="#059669" />
          <StatCard icon={Minus} label="Watch" value={s.watch ?? 0} color="#d97706" />
          <StatCard icon={ThumbsDown} label="At Risk" value={s.at_risk ?? 0} color="#dc2626" />
        </div>

        <div className="space-y-2">
          {data.scores.map(t => (
            <div key={t.tenant_id} className="border rounded-xl p-4" data-testid={`health-row-${t.tenant_id}`}>
              <div className="flex items-center gap-3 mb-2">
                <div className="h-9 w-9 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0"
                  style={{ backgroundColor: HEALTH_COLORS[t.status] || '#64748b' }}>
                  {t.overall}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{t.name}</div>
                  <div className="text-[10px] text-muted-foreground capitalize">{t.plan || 'no plan'} · {t.features_enabled} features · {t.usage_events_30d} events (30d)</div>
                </div>
                <Badge className="text-[9px] capitalize text-white shrink-0" style={{ backgroundColor: HEALTH_COLORS[t.status] }}>
                  {t.status.replace('_', ' ')}
                </Badge>
              </div>
              <div className="grid grid-cols-4 gap-2">
                {[['Login', t.login_score], ['Adoption', t.adoption_score], ['Usage', t.usage_score], ['Renewal', t.renewal_score]].map(([lbl, v]) => (
                  <div key={lbl}>
                    <div className="flex justify-between text-[9px] text-muted-foreground mb-0.5">
                      <span>{lbl}</span><span className="tabular-nums">{v}</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${v}%`, backgroundColor: v >= 70 ? '#059669' : v >= 40 ? '#d97706' : '#dc2626' }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </ScrollArea>
  );
}

// ═══ 3. EMAIL CAMPAIGNS ═══
const TARGET_LABELS = {
  all_admins: 'All Tenant Admins',
  specific_tenant: 'Specific Tenant',
  at_risk: 'At-Risk Tenants',
  new_tenants: 'New Tenants',
};

export function EmailCampaigns({ token }) {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [form, setForm] = useState({ name: '', subject: '', body_html: '', target: 'all_admins' });

  const fetchCampaigns = useCallback(() => {
    axios.get(`${BACKEND_URL}/api/admin/email-campaigns`, { headers })
      .then(r => setCampaigns(r.data.campaigns || []))
      .catch(() => toast.error('Failed to load campaigns'))
      .finally(() => setLoading(false));
  }, [headers]);

  useEffect(() => { fetchCampaigns(); }, [fetchCampaigns]);

  const createCampaign = async () => {
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/email-campaigns`, form, { headers });
      toast.success('Campaign created');
      setShowNew(false);
      setForm({ name: '', subject: '', body_html: '', target: 'all_admins' });
      fetchCampaigns();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const sendCampaign = async (id) => {
    if (!window.confirm('Send this campaign to its target recipients now?')) return;
    setSendingId(id);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/email-campaigns/${id}/send`, {}, { headers });
      toast.success(`Sent to ${res.data.sent} of ${res.data.total_recipients} recipients`);
      fetchCampaigns();
    } catch (err) { toast.error(err.response?.data?.detail || 'Send failed'); }
    finally { setSendingId(null); }
  };

  return (
    <ScrollArea className="h-full">
      <div className="p-6 max-w-4xl space-y-4" data-testid="sa-email-campaigns">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>Email Campaigns</h2>
            <p className="text-xs text-muted-foreground">Create and send campaigns to tenant admins via Resend</p>
          </div>
          <Button size="sm" className="gap-1.5 text-xs" onClick={() => setShowNew(true)} data-testid="new-campaign-btn">
            <Plus className="h-3.5 w-3.5" /> New Campaign
          </Button>
        </div>

        {loading ? (
          <div className="py-16 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" /></div>
        ) : campaigns.length === 0 ? (
          <div className="py-12 text-center border rounded-xl">
            <Mail className="h-8 w-8 text-muted-foreground/25 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">No campaigns yet.</p>
            <p className="text-xs text-muted-foreground mt-1">Create your first campaign to reach tenant admins.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {campaigns.map(c => (
              <div key={c.id} className="border rounded-xl p-4 flex items-center gap-3" data-testid={`campaign-${c.id}`}>
                <div className="h-9 w-9 rounded-lg bg-violet-100 flex items-center justify-center shrink-0">
                  <Mail className="h-4 w-4 text-violet-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{c.name || c.subject}</div>
                  <div className="text-[10px] text-muted-foreground truncate">
                    {c.subject} · {TARGET_LABELS[c.target] || c.target} · {(c.created_at || '').slice(0, 10)}
                  </div>
                </div>
                <Badge variant={c.status === 'sent' ? 'default' : 'outline'} className="text-[9px] capitalize shrink-0">
                  {c.status}{c.status === 'sent' ? ` (${c.sent_count})` : ''}
                </Badge>
                {c.status !== 'sent' && (
                  <Button size="sm" variant="outline" className="gap-1 text-xs shrink-0" disabled={sendingId === c.id}
                    onClick={() => sendCampaign(c.id)} data-testid={`send-campaign-${c.id}`}>
                    {sendingId === c.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />} Send
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}

        <Dialog open={showNew} onOpenChange={setShowNew}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Mail className="h-4 w-4" /> New Email Campaign</DialogTitle>
              <DialogDescription>Draft a campaign. You can send it after creating.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <Label className="text-xs">Campaign Name</Label>
                <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Q3 Product Update" data-testid="campaign-name-input" />
              </div>
              <div>
                <Label className="text-xs">Subject</Label>
                <Input value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))} placeholder="What's new in LodgiFlow" data-testid="campaign-subject-input" />
              </div>
              <div>
                <Label className="text-xs">Body (HTML supported)</Label>
                <Textarea rows={6} value={form.body_html} onChange={e => setForm(p => ({ ...p, body_html: e.target.value }))} placeholder="<p>Hello...</p>" data-testid="campaign-body-input" />
              </div>
              <div>
                <Label className="text-xs">Target</Label>
                <select value={form.target} onChange={e => setForm(p => ({ ...p, target: e.target.value }))}
                  className="w-full h-9 rounded-md border bg-background px-3 text-sm mt-1" data-testid="campaign-target-select">
                  {Object.entries(TARGET_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowNew(false)}>Cancel</Button>
              <Button onClick={createCampaign} disabled={saving || !form.name || !form.subject} className="gap-1.5" data-testid="create-campaign-btn">
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Create Draft
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </ScrollArea>
  );
}

// ═══ 5. USAGE METERING ═══
export function UsageMetering({ token }) {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [tenants, setTenants] = useState([]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${BACKEND_URL}/api/admin/usage/summary?days=${days}`, { headers }),
      axios.get(`${BACKEND_URL}/api/tenants`, { headers }).catch(() => ({ data: [] })),
    ])
      .then(([uRes, tRes]) => {
        setData(uRes.data);
        setTenants(tRes.data.tenants || tRes.data || []);
      })
      .catch(() => toast.error('Failed to load usage data'))
      .finally(() => setLoading(false));
  }, [headers, days]);

  const tenantName = (tid) => tenants.find(t => t.id === tid)?.name || tid || 'Unknown';

  const features = Object.entries(data?.by_feature || {}).sort((a, b) => b[1] - a[1]);
  const maxFeature = Math.max(...features.map(([, v]) => v), 1);
  const tenantRows = Object.entries(data?.by_tenant || {})
    .map(([tid, feats]) => ({ tid, total: Object.values(feats).reduce((s, v) => s + v, 0), feats }))
    .sort((a, b) => b.total - a.total);

  return (
    <ScrollArea className="h-full">
      <div className="p-6 max-w-4xl space-y-6" data-testid="sa-usage-metering">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>Usage Metering</h2>
            <p className="text-xs text-muted-foreground">Feature usage volume across tenants</p>
          </div>
          <div className="flex gap-1">
            {[7, 30, 90].map(d => (
              <button key={d} onClick={() => setDays(d)}
                className={`px-2.5 py-1 rounded-md text-[10px] font-medium transition-colors ${
                  days === d ? 'bg-primary text-primary-foreground' : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                }`} data-testid={`usage-days-${d}`}>
                {d}d
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="py-16 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" /></div>
        ) : !data ? (
          <p className="text-sm text-muted-foreground">No usage data available.</p>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <StatCard icon={TrendingUp} label="Total Events" value={(data.total_events || 0).toLocaleString()} sub={`last ${data.period_days} days`} color="#7c3aed" />
              <StatCard icon={Building2} label="Active Tenants" value={tenantRows.length} color="#2563eb" />
              <StatCard icon={RefreshCw} label="Features Used" value={features.length} color="#059669" />
            </div>

            <div>
              <h3 className="text-sm font-semibold mb-2">By Feature</h3>
              {features.length === 0 ? (
                <div className="py-10 text-center border rounded-xl">
                  <p className="text-sm text-muted-foreground">No usage events in this period.</p>
                </div>
              ) : (
                <div className="border rounded-xl p-4 space-y-2">
                  {features.map(([f, count]) => (
                    <div key={f} className="flex items-center gap-2 text-xs">
                      <span className="w-36 truncate font-medium capitalize">{f.replace(/_/g, ' ')}</span>
                      <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                        <div className="h-full rounded-full bg-violet-500 transition-all duration-500" style={{ width: `${(count / maxFeature) * 100}%` }} />
                      </div>
                      <span className="font-bold tabular-nums w-14 text-right">{count.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {tenantRows.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold mb-2">By Tenant</h3>
                <div className="border rounded-xl divide-y">
                  {tenantRows.map(row => (
                    <div key={row.tid} className="p-3" data-testid={`usage-tenant-${row.tid}`}>
                      <div className="flex items-center gap-2 text-sm mb-1">
                        <span className="font-medium flex-1">{tenantName(row.tid)}</span>
                        <span className="font-bold tabular-nums text-xs">{row.total.toLocaleString()} events</span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(row.feats).sort((a, b) => b[1] - a[1]).slice(0, 6).map(([f, c]) => (
                          <span key={f} className="text-[8px] px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 font-medium capitalize">{f.replace(/_/g, ' ')}: {c}</span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </ScrollArea>
  );
}

export function NpsFeedback({ token }) {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(() => {
    setLoading(true);
    axios.get(`${BACKEND_URL}/api/admin/feedback`, { headers })
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load feedback'))
      .finally(() => setLoading(false));
  }, [headers]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <div className="py-16 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" /></div>;
  if (!data) return <p className="p-6 text-sm text-muted-foreground">No feedback data available.</p>;

  const npsColor = data.nps >= 30 ? '#059669' : data.nps >= 0 ? '#d97706' : '#dc2626';

  return (
    <ScrollArea className="h-full">
      <div className="p-6 max-w-4xl space-y-6" data-testid="sa-nps-feedback">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>NPS & Feedback</h2>
            <p className="text-xs text-muted-foreground">Net Promoter Score and user feedback across all tenants</p>
          </div>
          <Button size="sm" variant="outline" className="gap-1.5 text-xs" onClick={fetchData}>
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </Button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard icon={MessageSquare} label="NPS Score" value={data.nps} sub={`${data.total_responses} responses`} color={npsColor} />
          <StatCard icon={ThumbsUp} label="Promoters" value={data.promoters} sub="score 9-10" color="#059669" />
          <StatCard icon={Minus} label="Passives" value={data.passives} sub="score 7-8" color="#d97706" />
          <StatCard icon={ThumbsDown} label="Detractors" value={data.detractors} sub="score 0-6" color="#dc2626" />
        </div>

        <div>
          <h3 className="text-sm font-semibold mb-2">Recent Feedback ({data.entries.length})</h3>
          {data.entries.length === 0 ? (
            <div className="py-12 text-center border rounded-xl">
              <MessageSquare className="h-8 w-8 text-muted-foreground/25 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">No feedback submitted yet.</p>
            </div>
          ) : (
            <div className="border rounded-xl divide-y">
              {data.entries.map((e, i) => (
                <div key={e.id || i} className="p-3 flex items-start gap-3 text-sm" data-testid={`feedback-entry-${i}`}>
                  {e.nps_score != null ? (
                    <div className="h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0"
                      style={{ backgroundColor: e.nps_score >= 9 ? '#059669' : e.nps_score >= 7 ? '#d97706' : '#dc2626' }}>
                      {e.nps_score}
                    </div>
                  ) : (
                    <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                      <MessageSquare className="h-3.5 w-3.5 text-muted-foreground" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium truncate">{e.email || 'Anonymous'}</span>
                      <span className="text-[9px] text-muted-foreground">{(e.timestamp || '').slice(0, 10)}</span>
                      {e.page && <Badge variant="outline" className="text-[8px]">{e.page}</Badge>}
                    </div>
                    {e.comment && <p className="text-xs text-muted-foreground mt-0.5">{e.comment}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </ScrollArea>
  );
}
